import { saveFailureKind } from './save-failure-kind.helper';

/**
 * The pure decision logic of the tenant theme editor view (feature plan 0054), kept out of the
 * element so it is testable without a DOM.
 */

export type ThemeLoadState = 'loading' | 'loaded' | 'failed';

export interface ThemeEditorState {
    load: ThemeLoadState;
    draft: string;
    saved: string;
    errorCount: number;
    saving: boolean;
}

export class TenantThemeEditorState {

    /** Editable only once the current stylesheet arrived — typing earlier would race the load. */
    public static isReadonly(state: ThemeEditorState): boolean {
        return state.load !== 'loaded';
    }

    public static isDirty(state: ThemeEditorState): boolean {
        return state.draft !== state.saved;
    }

    public static canSave(state: ThemeEditorState): boolean {
        return state.load === 'loaded'
            && !state.saving
            && state.errorCount === 0
            && TenantThemeEditorState.isDirty(state);
    }

    /** A draft with errors must not be applied even locally: it could load what saving refuses. */
    public static canPreview(state: ThemeEditorState): boolean {
        return state.load === 'loaded' && state.errorCount === 0;
    }

    /**
     * The fetched response is the theme only if it is OK and CSS. The SPA fallback route answers
     * unknown paths with the HTML shell (200, text/html): taking that as the "current theme" and
     * saving would overwrite the real one.
     */
    public static isThemeResponse(response: { ok: boolean; headers: { get(name: string): string | null } }): boolean {
        return response.ok && (response.headers.get('content-type') ?? '').toLowerCase().startsWith('text/css');
    }

    /** Which message a failed save shows: no permission (403) or a general failure. */
    public static saveFailureKind(error: unknown): 'forbidden' | 'failed' {
        return saveFailureKind(error);
    }
}
